/**
 * PhotoOnlyTemplate - Minimal photo template
 *
 * Displays only the photo image with selection indicators.
 * No metadata, description, or EXIF data displayed.
 *
 * Feature 047-photo-dedup-collection-sort: Applies dimming to orphaned photos
 * (photos not in any collection) using 60% opacity with hover effect.
 *
 * @module PhotoItem2/templates/PhotoOnlyTemplate
 */

import { cn } from '@/lib/utils';
import type { PhotoItem2TemplateProps } from '../PhotoItem2.types';
import { AdaptivePhoto } from '../../photos/AdaptivePhoto';
import { SelectionIndicator } from '@/components/atoms';
import '@/styles/orphaned-photos.css';

/**
 * PhotoOnlyTemplate component
 *
 * Renders a simple photo-only view with selection support.
 * Uses AdaptivePhoto for automatic resolution selection based on container size.
 */
export function PhotoOnlyTemplate({
  data,
  isSelected = false,
  selectable = false,
  zoom = 1.0, // NOTE: zoom prop no longer used - AdaptivePhoto uses container size
  onClick,
  onCritiqueClick: _onCritiqueClick, // Filter out non-DOM prop
  className,
  ...restProps
}: PhotoItem2TemplateProps) {
  void zoom; // Suppress unused variable warning - AdaptivePhoto uses container size instead
  void _onCritiqueClick; // Filter out - not a valid DOM prop

  // Feature 047-photo-dedup-collection-sort (T022): Apply dimming to orphaned photos
  const isOrphaned = data.photoData.collectionCount === 0;

  // Feature 081: photo-versioning - Forward additional props (e.g., onContextMenu) to div
  return (
    <div
      onClick={onClick}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-lg bg-surface-2 w-full h-full',
        selectable && 'cursor-pointer hover:ring-2 hover:ring-primary/50',
        isSelected && 'ring-4 ring-primary',
        isOrphaned && 'photo-card--orphaned',
        className
      )}
      {...restProps}
    >
      {/* Adaptive photo with automatic resolution selection */}
      <AdaptivePhoto
        photo={data.photoData}
        alt={data.photoData.description || ''}
        className="w-full h-full p-0.5"
        preferStorageUrl={true}
      />

      {/* Selection indicator */}
      <SelectionIndicator visible={isSelected} size="md" className="z-10" />
    </div>
  );
}
